// ============================================
// Fashion Plate v2 — Type Definitions
// Visual Prompt Builder for AI Nurse Personas
// Four-Prompt / Two-LLM Architecture
// ============================================

import type { ContextLayer, NurseTraitProfile, RegisterType } from './patientJourney';
import type { BiomarkerResult } from './index';

// --- Garment System ---

export type GarmentPiece = 'shoes' | 'heart' | 'brain' | 'hat' | 'dress' | 'accessories';

export type GarmentIntensity = 'minimal' | 'moderate' | 'full' | 'max';

/** A single garment piece with its resolved state */
export interface GarmentState {
  piece: GarmentPiece;
  label: string;               // "Soft slippers", "Business shoes", "Citation brain"
  intensity: GarmentIntensity;
  contextCategory: string;     // "Temporal / Pace", "Emotional / Relational", etc.
  activeVariables: Record<string, string>; // { timeOfDay: 'late_night', season: 'winter' }
}

/** Visual state options for a single garment piece */
export interface GarmentVariant {
  label: string;
  intensity: GarmentIntensity;
  description: string;         // "Approach speed. Gentle night slippers for 2am crisis."
  triggerValues: Record<string, string[]>; // which context values trigger this variant
}

/** Registry entry mapping a garment piece to its context variables */
export interface GarmentRegistryEntry {
  piece: GarmentPiece;
  contextCategory: string;
  variables: string[];         // keys from ContextLayer
  variants: GarmentVariant[];
}

// --- Nurse Persona ---

/** A dressed nurse persona — curated or custom */
export interface NursePersona {
  personaId: string;
  name: string;                // "The Night Nurse"
  garments: GarmentState[];
  contextSnapshot: ContextLayer;
  triggerConditions: string;   // human-readable: "Late night + High stress + Any literacy"
  conversationStyle: string;   // personality brief
  sampleExcerpt?: string;     // sample conversation line
  isCurated: boolean;
  createdBy: 'system' | 'operator';
  usageCount: number;
}

// --- Clinical Object (Captain → Nurse handoff) ---

/** The sealed clinical object from the Captain (Prompt 1) */
export interface ClinicalObject {
  biomarkers: BiomarkerResult[];
  protocol: { productId: string; productName: string; dosage: string; timing: string; reasoning: string; tier: 1 | 2 | 3 }[];
  gateStatus: 'gate1_active' | 'gate2_triggered' | 'gate2_not_indicated';
  interactionWarnings: string[];
  contraindications: string[];
  evidenceGrades: Record<string, string>;
}

// --- Prompt Layer System ---

export type PromptLayerId = 'founder' | 'vertical' | 'outfit';

/** A versioned prompt layer — one of the three Nurse prompts */
export interface PromptLayer {
  layerId: PromptLayerId;
  content: string;             // the actual prompt text
  version: number;
  lastModifiedBy: string;
  lastModifiedAt: string;
}

/** A message in a prompt layer chat */
export interface PromptChatMessage {
  role: 'operator' | 'layer';
  content: string;
  layerId: PromptLayerId;
  timestamp: string;
  garmentChanges?: GarmentState[]; // garments that changed from this message
}

/** Result of chatting with a prompt layer */
export interface PromptChatResult {
  response: string;
  promptUpdates?: Partial<PromptLayer>;
  garmentChanges?: GarmentState[];
  matchedPersona?: string;     // personaId if chat produced a known persona config
}

// --- Gate Configuration ---

export type GateStrategy = 'standard' | 'direct_to_consult' | 'modified_gate1' | 'full_stack';

/** Per-archetype gate configuration */
export interface GateConfig {
  configId: string;
  archetypeId: string;
  archetypeName: string;
  gateStrategy: GateStrategy;
  description: string;         // "Supplements as gate destroy trust. Provider-first builds credibility."
  approvedByMD: boolean;
  approvedAt?: string;
  syntheticArmyData?: {
    passRate: number;
    conversionRate: number;
    sampleSize: number;
  };
}

// --- Arc Diagnostics ---

export type ArcFailureStage = 'marketing_mismatch' | 'wrong_outfit' | 'gate_resistance' | 'close_failure';

/** Diagnosis of where in the arc trust/conversion broke */
export interface ArcDiagnostic {
  diagnosticId: string;
  runId: string;
  failureStage: ArcFailureStage;
  description: string;         // "The Evidence Queen should have handled this skeptic, not the Gentle Teacher."
  suggestedAction: string;     // "Adjust Stage 2 (Prompt 4)."
  createdAt: string;
}

// --- Concern Cards (Clinical Capture Strip) ---

export type ConcernStatus = 'detected' | 'flagged' | 'borderline' | 'clear';

/** A real-time concern card in the Clinical Capture Strip */
export interface ConcernCard {
  cardId: string;
  concern: string;             // "fatigue", "weight gain", "dismissed by doctor"
  source: 'patient_mention' | 'lab_result';
  status: ConcernStatus;
  detectedAt: string;
  linkedBiomarker?: string;    // biomarker name if lab-linked
}

// --- Fashion Plate UI State ---

export type FashionPlatePhase = 'gallery' | 'dressing' | 'conversation' | 'demo';

/** Fashion Plate component state */
export interface FashionPlateState {
  phase: FashionPlatePhase;
  activePersona: NursePersona | null;
  garments: GarmentState[];
  promptLayers: {
    founder: PromptLayer;
    vertical: PromptLayer;
    outfit: PromptLayer;
  };
  chatHistories: {
    founder: PromptChatMessage[];
    vertical: PromptChatMessage[];
    outfit: PromptChatMessage[];
  };
  concernCards: ConcernCard[];
  isDemoMode: boolean;
}

// --- Tap-to-Instruct (Prompt Maker) ---

export type TapRegion = 'brain' | 'heart' | 'personality' | 'dress';

/** Input to the prompt maker — operator instruction + current state */
export interface PromptMakerInput {
  instruction: string;
  tapRegion?: TapRegion;
  currentGarments: GarmentState[];
  currentContext: Partial<ContextLayer>;
  currentTraitProfile?: NurseTraitProfile;
}

/** Output from the prompt maker — what changed */
export interface PromptMakerOutput {
  layerUpdates: Partial<Record<PromptLayerId, Partial<PromptLayer>>>;
  garmentChanges: GarmentState[];
  traitAdjustments?: Partial<NurseTraitProfile>;
  contextUpdates?: Partial<ContextLayer>;
  nurseConfirmation: string;
}

// --- Clinical Capture Strip (Section 8) ---

export type CaptureCategory = 'symptom' | 'medication' | 'life_event' | 'context' | 'history';

/** A clinical signal extracted from patient conversation */
export interface CapturedSignal {
  id: string;
  term: string;                  // "Fatigue", "SSRI", "Divorce"
  category: CaptureCategory;
  source: 'patient_mention' | 'nurse_inference' | 'lab_correlation';
  confidence: number;            // 0-1
  detectedAtTurn: number;
  linkedBiomarker?: string;
  reinforcedAtTurns: number[];
  sourceText?: string;             // Patient's actual words that triggered this signal
}

export type CaptureStyle = 'clinical' | 'conversational' | 'minimal';

/** Configuration for the Clinical Capture Strip */
export interface CaptureConfig {
  style: CaptureStyle;
  reinforcementFrequency: number;    // every N pills, nurse references them
  conversionThreshold: number;       // pill count before save prompt
  providerHandoffEnabled: boolean;   // include pills in Level 2/3 payload
}

export const DEFAULT_CAPTURE_CONFIG: CaptureConfig = {
  style: 'conversational',
  reinforcementFrequency: 3,
  conversionThreshold: 5,
  providerHandoffEnabled: true,
};

// --- Nurse Config (Firestore: nurse_configs collection, Section 12.1) ---

export interface NurseConfig {
  configId: string;
  brandId: string;
  name: string;                      // "Night Nurse v3"
  isActive: boolean;
  foundationVersion: number;         // reference, not editable post-launch
  vertical: {
    gateStrategy: GateStrategy;
    supplementTimingTurn: number;     // when to introduce products
    evidenceDepth: 'simple' | 'moderate' | 'full';
    clinicalPosture: string;         // freeform from prompt maker
  };
  texture: {
    traits: NurseTraitProfile;
    registerDefaults: {
      phase1: RegisterType;
      phase2: RegisterType;
    };
    contextOverrides: Record<string, string>;
    personality: string;             // freeform from prompt maker
  };
  captureConfig: CaptureConfig;
  imageParams: {
    heartColor: string;
    auraStyle: string;
    brainVisualization: string;
    authorityPosture: string;
    dressStyle: string;
    accessories: string;
  };
  lastTestRunId?: string;
  lastPassRate?: number;
  createdAt: string;
  updatedAt: string;
}

// --- Fashion Plate Document (Firestore: fashion_plates collection) ---

/** A complete Fashion Plate stored in Firestore — persona + locked prompt layers */
export interface FashionPlateDocument {
  plateId: string;
  name: string;                        // "Dr. C Default"
  description: string;                 // human-readable purpose
  isDefault: boolean;                  // only one may be true
  persona: NursePersona;
  lockedLayers: {
    foundation: string;                // Prompt 2 content — locked pre-launch
    vertical: string;                  // Prompt 3 template — MD-approved clinical rules
  };
  traitProfile: NurseTraitProfile;
  registerDefaults: {
    phase1: RegisterType;
    phase2: RegisterType;
  };
  captureConfig: CaptureConfig;
  approvedBy: string;                  // "Dr. Christopher Calapai, D.O."
  approvedAt: string;
  createdAt: string;
  updatedAt: string;
}
